import { useEffect, useMemo, useState } from 'react'
import { Link, useLocation } from 'react-router-dom'
import { debounce } from 'lodash-es'

import {
  IEventTableMeta,
  useGetEventTablesForDataViewQuery
} from 'app/services/api/datamanagement/eventTableAttributes'
import LoadingSpinner from 'common/components/LoadingSpinner'
import OverlayScrollbar from 'common/components/OverlayScrollBar'
import { SearchInput } from 'common/components/SearchInput'
import { IDataViewProps } from 'features/DataManagement/DataView'

interface ISortedEventTables {
  [key: string]: IEventTableMeta[]
}

const ViewsTabContent = ({ dvBasePath }: IDataViewProps) => {
  const { search } = useLocation()
  const [searchText, setSearchText] = useState<string>('')
  const [displayText, setDisplayText] = useState<string>('')
  const [groupedTables, setGroupedTables] = useState<ISortedEventTables>({})

  const {
    currentData: dataViewEventTables,
    isLoading: isDataViewAttributesLoading
  } = useGetEventTablesForDataViewQuery()

  /*
    The search bar in this component uses the debounce function. This optimizes searching
    for event tables as it prevents triggering a search every keystroke

    When unit testing with debounce make sure that you have a short timeout after your
    user input
  */

  const handleChangeStart = (event: React.ChangeEvent<HTMLInputElement>) => {
    setDisplayText(event.target.value)
    debounceHandleChange(event)
  }

  const debounceHandleChange = useMemo(() => {
    // Triggers a search 200 milliseconds after the user types his/her last character
    return debounce((e) => setSearchText(e.target.value), 200)
  }, [])

  useEffect(() => {
    // Cleans up pending debounce if component unmounts
    return () => {
      debounceHandleChange.cancel()
    }
  }, [debounceHandleChange])

  /*
   * Reruns the search filter for event tables that share the same string as whats in the search bar
   * NOTE: this search is case Insensitive
   */
  useEffect(() => {
    if (searchText.length !== 0) {
      const filter = searchText.toLowerCase()

      const filtered = dataViewEventTables?.filter((table: IEventTableMeta) => {
        const tableName = table?.name.toLowerCase()

        return tableName.includes(filter)
      })

      setGroupedTables(groupEventTables(filtered))
    } else {
      setGroupedTables(groupEventTables(dataViewEventTables ?? []))
    }
  }, [searchText, dataViewEventTables])

  /*
   * This function will take in an array of eventTables and group each event table
   * depending on its dataManagementCategory.categoryName property and returns an
   * object with each event table category name which holds an array with its respective
   * event tables
   */
  const groupEventTables = (eventTable: IEventTableMeta[] | undefined) => {
    return eventTable?.reduce(function (previous, current) {
      previous[current?.dataManagementCategory?.categoryName] =
        previous[current?.dataManagementCategory?.categoryName] || []
      previous[current.dataManagementCategory?.categoryName].push(current)

      return previous
    }, Object.create(null))
  }

  // Component for each data management category name and also renders each of its respective event tables
  const generateSection = (
    categoryName: string,
    index: number
  ): JSX.Element => {
    return (
      <div className='my-8' key={index}>
        <span className='text-2xl font-semibold'>{categoryName}</span>
        <div
          className={[
            'grid min-w-full',
            'grid-cols-5 gap-4 py-6',
            '2xl:grid-cols-6'
          ].join(' ')}
        >
          {groupedTables[categoryName]
            .sort((a, b) => a.name.localeCompare(b.name))
            ?.map((eventTable: IEventTableMeta) => (
              <div
                key={eventTable.id}
                className={[
                  'flex h-12 content-center',
                  'items-center justify-center',
                  'rounded-sm bg-white',
                  'align-middle shadow-md'
                ].join(' ')}
              >
                <Link
                  className={[
                    'row-space-between flex',
                    'h-full w-full',
                    'items-center px-3'
                  ].join(' ')}
                  to={`${dvBasePath}/details/views/${eventTable.id}${search}`}
                >
                  <div
                    className={[
                      'font-medium',
                      'items-center truncate px-2',
                      'leading-normal text-gray-900',
                      'text-ellipsis'
                    ].join(' ')}
                    title={eventTable.name}
                  >
                    {eventTable.name}
                  </div>
                </Link>
              </div>
            ))}
        </div>
      </div>
    )
  }

  return (
    <LoadingSpinner isLoading={isDataViewAttributesLoading}>
      <div className='flex h-full flex-1 flex-col'>
        <div className='sticky top-0 mx-28 pt-4 pb-2'>
          <SearchInput
            className='w-72'
            placeholder='Search'
            onChange={handleChangeStart}
            value={displayText}
          ></SearchInput>
        </div>
        <OverlayScrollbar>
          <div className='mx-28'>
            <div>
              {!isDataViewAttributesLoading && (
                <div>
                  {Object.keys(groupedTables).length > 0
                    ? Object.keys(groupedTables)
                        .sort((a, b) => a.localeCompare(b))
                        .map((key, index) => generateSection(key, index))
                    : searchText.length > 0 && (
                        <div className='mt-8 text-xl font-semibold'>
                          No views match the search criteria
                        </div>
                      )}
                </div>
              )}
            </div>
          </div>
        </OverlayScrollbar>
      </div>
    </LoadingSpinner>
  )
}

export default ViewsTabContent
